import { cva, type VariantProps } from 'class-variance-authority';
import * as React from 'react';

import { cn } from '../lib/utils';

import { Body } from './typography';

const vInputVariants = cva(
  'rounded-lg border bg-card px-3 py-2 text-sm text-foreground outline-none placeholder:text-muted-foreground disabled:cursor-not-allowed disabled:opacity-50',
  {
    variants: {
      variant: {
        default: 'border-input focus:border-primary-600',
        error: 'border-error-600 focus:border-error-600',
      },
    },
    defaultVariants: { variant: 'default' },
  },
);

type VInputProps = Omit<React.ComponentPropsWithoutRef<'input'>, 'size'> &
  VariantProps<typeof vInputVariants> & {
    label?: string;
    error?: string;
  };

export const VInput = React.forwardRef<HTMLInputElement, VInputProps>(
  function VInput({ className, variant, label, error, id, ...props }, ref) {
    const generatedId = React.useId();
    const inputId = id ?? generatedId;
    const resolvedVariant = error ? 'error' : variant;

    const input = (
      <input
        ref={ref}
        id={inputId}
        aria-invalid={error ? true : undefined}
        className={cn(vInputVariants({ variant: resolvedVariant, className }))}
        {...props}
      />
    );

    if (!label && !error) return input;

    return (
      <div className="flex flex-col gap-1.5">
        {label && (
          <Body as="label" htmlFor={inputId} weight="medium">
            {label}
          </Body>
        )}
        {input}
        {error && (
          <Body as="span" tone="error">
            {error}
          </Body>
        )}
      </div>
    );
  },
);
VInput.displayName = 'VInput';

export { vInputVariants };
export type { VInputProps };
